import { db } from '../../services/firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

const SETTINGS_COLLECTION = 'appSettings';
const SETTINGS_DOC_ID = 'general';

/**
 * Récupère les paramètres de l'application depuis Firestore
 * @returns {Promise<object>} - Les paramètres de l'application
 */
export const getAppSettings = async () => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return settingsDoc.data();
    }
    
    // Si les paramètres n'existent pas encore, créer les paramètres par défaut
    const defaultSettings = {
      transcriptionService: 'assemblyAI', // 'assemblyAI' ou 'openAI'
      assemblyAIApiKey: '',
      openAIApiKey: '',
      assemblyAISettings: {
        enableDiarization: true,
        maxSpeakers: 2,
        enablePunctuation: true,
        enableFormatting: true,
        enableWordDetails: true,
        languageCode: 'fr'
      },
      openAISettings: {
        model: 'whisper-1',
        language: 'fr',
        summaryPrompt: '',
      },
      // Paramètres d'email pour les notifications
      emailSettings: {
        enabled: false,
        sharedMailbox: '', // Adresse de la boîte partagée pour l'envoi des emails
      },
      appUrl: 'https://diotranscript.firebaseapp.com', // URL de base de l'application
      updatedAt: new Date()
    };
    
    await setDoc(settingsRef, defaultSettings);
    return defaultSettings;
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour les paramètres de l'application dans Firestore
 * @param {object} newSettings - Les nouveaux paramètres à enregistrer
 * @returns {Promise<object>} - Les paramètres mis à jour
 */
export const updateAppSettings = async (newSettings) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const settingsDoc = await getDoc(settingsRef);
    
    const updatedSettings = {
      ...newSettings,
      updatedAt: new Date()
    };
    
    if (settingsDoc.exists()) {
      await updateDoc(settingsRef, updatedSettings);
    } else {
      await setDoc(settingsRef, updatedSettings);
    }
    
    return updatedSettings;
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour les paramètres AssemblyAI dans Firestore
 * @param {object} assemblyAISettings - Nouveaux paramètres AssemblyAI
 * @returns {Promise} - Promise résolue après la mise à jour
 */
export const updateAssemblyAISettings = async (assemblyAISettings) => {
  try {
    // Récupérer d'abord les paramètres existants
    const settingsRef = doc(db, 'appSettings', 'general');
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      // Mettre à jour uniquement les paramètres AssemblyAI
      await updateDoc(settingsRef, {
        assemblyAISettings: {
          ...assemblyAISettings
        },
        updatedAt: serverTimestamp()
      });
      
      return true;
    } else {
      // Créer le document s'il n'existe pas
      await setDoc(settingsRef, {
        assemblyAISettings: {
          ...assemblyAISettings
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return true;
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour les paramètres de résumé dans Firestore
 * @param {object} summarySettings - Nouveaux paramètres de résumé
 * @returns {Promise} - Promise résolue après la mise à jour
 */
export const updateSummarySettings = async (summarySettings) => {
  try {
    // Récupérer d'abord les paramètres existants
    const settingsRef = doc(db, 'appSettings', 'general');
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      // Mettre à jour uniquement les paramètres de résumé
      await updateDoc(settingsRef, {
        summarySettings: {
          ...summarySettings
        },
        updatedAt: serverTimestamp()
      });
      
      return true;
    } else {
      // Créer le document s'il n'existe pas
      await setDoc(settingsRef, {
        summarySettings: {
          ...summarySettings
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return true;
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour uniquement les paramètres OpenAI
 * @param {object} openAISettings - Les paramètres OpenAI à mettre à jour
 * @returns {Promise<object>} - Les paramètres mis à jour
 */
export const updateOpenAISettings = async (openAISettings) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      await getAppSettings(); // Initialiser avec les paramètres par défaut
    }
    
    const currentSettings = settingsDoc.exists() ? settingsDoc.data() : await getAppSettings();
    
    const updatedSettings = {
      ...currentSettings,
      openAISettings: {
        ...currentSettings.openAISettings,
        ...openAISettings,
        summaryPrompt: openAISettings.summaryPrompt !== undefined ? openAISettings.summaryPrompt : (currentSettings.openAISettings?.summaryPrompt || '')
      },
      updatedAt: new Date()
    };
    
    await updateDoc(settingsRef, updatedSettings);
    return updatedSettings;
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour uniquement les paramètres d'email
 * @param {object} emailSettings - Les paramètres d'email à mettre à jour
 * @returns {Promise<object>} - Les paramètres mis à jour
 */
export const updateEmailSettings = async (emailSettings) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      await getAppSettings(); // Initialiser avec les paramètres par défaut
    }
    
    const currentSettings = settingsDoc.exists() ? settingsDoc.data() : await getAppSettings();
    
    const updatedSettings = {
      ...currentSettings,
      emailSettings: {
        ...currentSettings.emailSettings,
        ...emailSettings
      },
      updatedAt: new Date()
    };
    
    await updateDoc(settingsRef, updatedSettings);
    return updatedSettings;
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère toutes les clés API utilisées par l'application
 * @returns {Promise<Object>} Objet contenant toutes les clés API
 */
export const getApiKeys = async () => {
  try {
    const appSettings = await getAppSettings();
    
    // Structure pour centraliser toutes les clés API
    const apiKeys = {
      assemblyAI: {
        apiKey: appSettings?.assemblyAISettings?.apiKey || '',
      },
      openAI: {
        apiKey: appSettings?.openAISettings?.apiKey || '',
        organizationId: appSettings?.openAISettings?.organizationId || '',
      }
    };
    
    return apiKeys;
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour la clé API AssemblyAI
 * @param {string} apiKey - Nouvelle clé API AssemblyAI
 * @returns {Promise<boolean>} - Succès de l'opération
 */
export const updateAssemblyAIApiKey = async (apiKey) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      await getAppSettings(); // Initialiser avec les paramètres par défaut
    }
    
    // Mise à jour de la clé dans la structure existante
    await updateDoc(settingsRef, {
      'assemblyAISettings.apiKey': apiKey,
      updatedAt: serverTimestamp()
    });
    
    // Également mettre à jour dans le champ global pour la rétrocompatibilité
    await updateDoc(settingsRef, {
      'assemblyAIApiKey': apiKey,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour les clés API OpenAI
 * @param {Object} apiKeys - Clés API OpenAI
 * @param {string} apiKeys.apiKey - Clé API OpenAI
 * @param {string} [apiKeys.organizationId] - ID d'organisation OpenAI (optionnel)
 * @returns {Promise<boolean>} - Succès de l'opération
 */
export const updateOpenAIApiKeys = async ({ apiKey, organizationId = '' }) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      await getAppSettings(); // Initialiser avec les paramètres par défaut
    }
    
    // Mise à jour des clés dans la structure openAISettings
    const updateData = {
      'openAISettings.apiKey': apiKey,
      updatedAt: serverTimestamp()
    };
    
    if (organizationId) {
      updateData['openAISettings.organizationId'] = organizationId;
    }
    
    await updateDoc(settingsRef, updateData);
    
    // Également mettre à jour dans le champ global pour la rétrocompatibilité
    await updateDoc(settingsRef, {
      'openAIApiKey': apiKey,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {

    throw error;
  }
}; 